import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { environment } from '../../../environments/environment';
import { IShift } from '../interfaces';
import { ShiftStatusConst } from '../constant';
import { StorageService } from './storage.service';

export interface IShiftDashboardTodayStats {
  date: string;
  todayTotal: number;
  todayOpen: number;
  coveragePct: number;
  inProgress: number;
  cancelled: number;
  noShow: number;
  breakdown: Record<string, number>;
}

@Injectable({
  providedIn: 'root'
})
export class ShiftsService {
  private shiftsSubject = new BehaviorSubject<IShift[]>([]);
  private shiftHistories = new BehaviorSubject<any[]>([]);
  shifts$ = this.shiftsSubject.asObservable();
  shiftHistories$ = this.shiftHistories.asObservable();

  private sseController?: AbortController;

  constructor(private http: HttpClient, private storageService: StorageService) {}

  getDashboardTodayStats(date?: string) {
    const query = date ? `?date=${encodeURIComponent(date)}` : '';
    return this.http.get<IShiftDashboardTodayStats>(`${environment.apiUrl}shifts/dashboard/today${query}`);
  }

  loadShifts(startDate: string, endDate: string): void {
    this.http.get<IShift[]>(`${environment.apiUrl}shifts?startDate=${startDate}&endDate=${endDate}`).subscribe(shifts => {
      this.shiftsSubject.next(shifts);
    });
  }

  loadShiftsStatus(startDate: string, endDate: string, status: string) {
    this.http.get<IShift[]>(`${environment.apiUrl}shifts?startDate=${startDate}&endDate=${endDate}&status=${status}`).subscribe(shifts => {
      this.shiftsSubject.next(shifts);
    });
  }

  loadHistories(shiftId: string) {
    this.http.get(`${environment.apiUrl}shifts/${shiftId}/histories`).subscribe((histories: any) => {
      this.shiftHistories.next(histories);
    });
  }

  getHistories(shiftId: string): Observable<any[]> {
    return this.http.get<any[]>(`${environment.apiUrl}shifts/${shiftId}/histories`);
  }

  getById(shiftId: string): Observable<IShift> {
    return this.http.get<IShift>(`${environment.apiUrl}shifts/${shiftId}`);
  }

  unassign(shiftId: string, reason: string) {
    return this.http.delete(`${environment.apiUrl}shifts/${shiftId}/assign`, { body: { reason } }).pipe(
      map(response => response)
    )
  }

  updateStatus(shiftId: string, status: ShiftStatusConst, reason: string) {
    return this.http.patch(`${environment.apiUrl}shifts/${shiftId}/status`, { status, reason }).pipe(
      map(response => response)
    )
  }

  updateTotalValue(shiftId: string, totalValue: number) {
    return this.http.patch(`${environment.apiUrl}shifts/${shiftId}/total-value`, { totalValue }).pipe(
      map(response => response)
    )
  }

  changeNewCredited(shiftId: string, professionalId: string) {
    return this.http.patch(`${environment.apiUrl}shifts/${shiftId}/credited`, { professionalId }).pipe(
      map(response => response)
    )
  }

  changeNewAssigned(shiftId: string, professionalId: string) {
    return this.http.patch(`${environment.apiUrl}shifts/${shiftId}/assigned`, { professionalId }).pipe(
      map(response => response)
    )
  }

  listenToShiftsSSE() {
    if (this.sseController) {
      this.sseController.abort();
    }
    this.sseController = new AbortController();

    const user = this.storageService.getUser();
    const token = user.token;
    const url = `${environment.apiUrl}shifts/stream`;

    fetchEventSource(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'text/event-stream'
      },
      signal: this.sseController.signal,
      onmessage: (ev) => {
        try {
          const data = JSON.parse(ev.data);
          this.updateShiftsFromSSE(data);
        } catch (e) {

        }
      },
      onerror: (err) => {

      },
      onclose: () => {

      }
    });
  }

  stopShiftsSSE() {
    this.sseController?.abort();
  }

  private updateShiftsFromSSE(sseData: any) {
    const current = this.shiftsSubject.getValue();
    let updated: IShift[];

    switch (sseData.type) {
      case 'shift_create':
        updated = [...current, sseData.shift];
        break;
      case 'shift_update':
        updated = current.map(s =>
          s.id === sseData.shift.id ? { ...s, ...sseData.shift } : s
        );
        break;
      case 'shift_delete':
        updated = current.filter(s => s.id !== sseData.shift.id);
        break;
      default:
        updated = current;
    }

    this.shiftsSubject.next(updated);
  }
}
